import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { chancenWerkMailer } from '@chancenwerk/server/mailer.js';
import { hefteContactRequestEmail } from '@chancenwerk/server/mailTemplates.js';
import { ContactRequest } from '../ContactRequest.js';
import { LogistikSettings } from '../LogistikClearanceSettings.js';
import { LogistikSchoolAccessToken } from '../tokens.js';

const requestContactOp = registerMutation({
  type: () => String,
  operationName: 'requestContactOp',
  options: {},
  params: [
    { type: () => String, name: 'email' },
    { type: () => String, name: 'tel' },
    { type: () => ID, name: 'tokenID' },
  ],
  resolve: (email: Maybe<string>, tel: Maybe<string>, tokenID: Maybe<string>) => async () => {
    const cr = new ContactRequest({
      email,
      tel,
      tokenID,
    });
    await cr.validate();
    const id = await cr.create();
    if (!id) {
      throw new Error('Failed to create contact request');
    }
    const recipient = await LogistikSettings.get()({ maxOrderAmountSupportEmail: 1 })
      .then(s => s.maxOrderAmountSupportEmail)
      .then(e => (e ? new Recipient(e) : null));
    if (!recipient) {
      throw new Error('Max order amount support email not found');
    }
    if (!tokenID) {
      throw new Error('Token ID not found');
    }
    const token = await getByID(LogistikSchoolAccessToken, tokenID)({ schoolName: 1, code: 1 });
    if (!token) {
      throw new Error('Token not found');
    }
    if (!token.schoolName || !token.code) {
      throw new Error('Token school name or code not found');
    }
    await chancenWerkMailer
      .sendMail(
        await Message.compose({
          subject: `Anfrage von ${token.schoolName} (${token.code})`,
          html: hefteContactRequestEmail({
            schoolName: token.schoolName,
            code: token.code,
            email: cr.email,
            tel: cr.tel,
          }),
        }),
        [recipient, ...(cr.email ? [new Recipient(cr.email)] : [])],
      )
      .catch(err => {
        logger.error({ err }, 'Fehler beim Senden einer Benachrichtigungsemail');
        throw err;
      });
    return id;
  },
});
export namespace ContactRequestAPI {
  export const requestContact = requestContactOp;
}
